/**
 * MER-492 PR C: client-side logic for the admin Users panel.
 *
 * The trust boundary is the SECURITY DEFINER pair from migration 0044:
 * `admin_request_action` re-validates every tenant_user_* payload
 * (slug shape, email regex, role allowlist, user_id uuid) and
 * `admin_list_tenant_users` is staff-gated server-side (non-staff get
 * zero rows). Everything here exists to give staff fast feedback
 * BEFORE the round-trip and to normalize what comes back.
 *
 * Plain .mjs so the node:test suite imports it directly (the
 * adminActions.mjs precedent); tenantUsers.d.mts types it for React.
 * Pure helpers (buildInvitePayload, summarizeInviteResult) take data
 * in / data out; listTenantUsers takes the supabase client as the
 * first argument so tests can hand in a mock.
 */

/** The two roles the panel can assign (MER-492 decision 9.1):
 * staff-viewer is never assignable through this surface. Mirrors the
 * role allowlist in migration 0044. */
export const ROLE_OPTIONS = ["owner", "viewer"];

/** Mirror of the migration-0044 email check: one @, a dot in the
 * domain, no whitespace. The RPC re-validates; this only exists so a
 * typo fails before the round-trip. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirror of the migration-0044 user_id check (the RPC raises
 * invalid_user_id, errcode 22023, on anything that is not a uuid).
 * Lives here, beside EMAIL_PATTERN, so adminActions.mjs and the
 * tenant-user payload builders share one definition. */
export const USER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function trimmed(value) {
  return typeof value === "string" ? value.trim() : "";
}

function stringOrNull(value) {
  return typeof value === "string" && value ? value : null;
}

/**
 * One row per tenant member from the staff-gated
 * admin_list_tenant_users RPC (migration 0044). Revoked rows ARE
 * included; the panel renders them behind a toggle.
 *
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {string} tenantId
 * @returns {Promise<Array<{
 *   userId: string,
 *   email: string | null,
 *   userName: string | null,
 *   role: string | null,
 *   memberSince: string | null,
 *   invitedAt: string | null,
 *   revokedAt: string | null,
 *   handoffDoneAt: string | null,
 *   lastSignInAt: string | null,
 * }>>}
 */
export async function listTenantUsers(supabase, tenantId) {
  if (typeof tenantId !== "string" || !tenantId) {
    throw new Error("listTenantUsers requires a tenant id");
  }
  const { data, error } = await supabase.rpc("admin_list_tenant_users", {
    p_tenant_id: tenantId,
  });
  if (error) {
    throw new Error(`listTenantUsers failed: ${error.message}`);
  }
  const rows = Array.isArray(data) ? data : [];
  const out = [];
  for (const r of rows) {
    if (!isPlainObject(r)) continue;
    const userId = stringOrNull(r.user_id);
    // A membership row without a user id is unrenderable and
    // unactionable (every write action keys on user_id); skip it.
    if (!userId) continue;
    out.push({
      userId,
      email: stringOrNull(r.email),
      userName: stringOrNull(r.user_name),
      role: stringOrNull(r.role),
      memberSince: stringOrNull(r.member_since),
      invitedAt: stringOrNull(r.invited_at),
      revokedAt: stringOrNull(r.revoked_at),
      handoffDoneAt: stringOrNull(r.handoff_done_at),
      lastSignInAt: stringOrNull(r.last_sign_in_at),
    });
  }
  return out;
}

/**
 * Validate + normalize the invite form into the tenant_user_invite
 * RPC payload (the buildCreatePayload precedent).
 *
 * Payload key rules (frozen contract with the PR B executor):
 * slug/email/role always; display_name only when non-empty;
 * reinvite only when exactly true (explicit re-invite of a REVOKED
 * member, after a confirm).
 *
 * @param {object} form  raw form values: { slug, email, role,
 *   displayName?, reinvite? }
 * @returns {{ ok: true, payload: object } | { ok: false, errors: Record<string, string> }}
 */
export function buildInvitePayload(form) {
  const src = isPlainObject(form) ? form : {};
  const errors = {};

  const slug = trimmed(src.slug);
  if (!slug) errors.slug = "Customer slug is required.";

  const email = trimmed(src.email);
  if (!email) {
    errors.email = "Email is required.";
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Enter a valid email address.";
  }

  const role = typeof src.role === "string" ? src.role : "";
  if (!ROLE_OPTIONS.includes(role)) {
    errors.role = "Pick owner or viewer.";
  }

  const displayName = trimmed(src.displayName);

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const payload = { slug, email, role };
  if (displayName) payload.display_name = displayName;
  if (src.reinvite === true) payload.reinvite = true;
  // MER-516: explicit confirm to bypass the cross-context guard (internal
  // address, or a user already on another tenant).
  if (src.confirmUnusualMember === true) payload.confirm_unusual_member = true;
  return { ok: true, payload };
}

/**
 * Extract the invite-specific outcome fields from a terminal
 * admin_action row (same row shape summarizeActionResult reads; this
 * pulls the fields it ignores). Never throws: rows from before
 * MER-492 (or malformed ones) degrade to nulls/false.
 *
 * action_link is PRESENT ONLY when the executor generated a one-time
 * link; the panel shows it once and never logs it.
 *
 * @param {unknown} actionRow
 * @returns {{
 *   emailSent: boolean,
 *   delivery: string | null,
 *   actionLink: string | null,
 *   userId: string | null,
 * }}
 */
export function summarizeInviteResult(actionRow) {
  const row = isPlainObject(actionRow) ? actionRow : {};
  const result = isPlainObject(row.result) ? row.result : {};
  const emailSent = result.email_sent === true;
  const delivery = stringOrNull(result.delivery);
  const rawLink = typeof result.action_link === "string" ? result.action_link.trim() : "";
  const actionLink = rawLink || null;
  const userId = stringOrNull(result.user_id);
  return { emailSent, delivery, actionLink, userId };
}

/** Link types the dashboard's /set-password route can verify. Mirror of
 * APP_VERIFIABLE_LINK_TYPES in admin/auth_admin.py and of the two email
 * templates in AUTH_EMAIL_SETUP.md: the route passes `type` straight to
 * verifyOtp and ignores anything else, so a magiclink can only travel as
 * the raw GoTrue action_link. */
export const APP_LINK_TYPES = ["invite", "recovery"];

/**
 * MER-539: validate + normalize the regenerate-link request into the
 * tenant_user_regenerate_link RPC payload.
 *
 * The action targets an EXISTING member by user_id. No email is sent
 * and none is accepted: an email in the payload would decide where the
 * credential goes, so the executor reads it off the auth user instead.
 *
 * confirm_regenerate is emitted as the literal true and only when the
 * caller passed exactly true (strict, matching the migration-0060
 * jsonb check and the executor's `is True`). It is never defaulted on:
 * the panel sets it from a click-through confirm, and the flag is the
 * audit-log evidence that the confirm happened.
 *
 * @param {object} form  raw values: { slug, userId, confirmRegenerate }
 * @returns {{ ok: true, payload: object } | { ok: false, errors: Record<string, string> }}
 */
export function buildRegenerateLinkPayload(form) {
  const src = isPlainObject(form) ? form : {};
  const errors = {};

  const slug = trimmed(src.slug);
  if (!slug) errors.slug = "Customer slug is required.";

  const userId = trimmed(src.userId);
  if (!userId) {
    errors.userId = "A member is required.";
  } else if (!USER_ID_PATTERN.test(userId)) {
    errors.userId = "Member id is not a uuid.";
  }

  if (src.confirmRegenerate !== true) {
    errors.confirmRegenerate = "Confirm the regenerate before it can be requested.";
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return { ok: true, payload: { slug, user_id: userId, confirm_regenerate: true } };
}

/**
 * MER-539: extract the regenerate-link outcome from a terminal
 * admin_action row. Never throws; anything malformed degrades to nulls.
 *
 * Both actionLink and tokenHash are link-equivalent secrets: the panel
 * shows them once and never logs them.
 *
 * @param {unknown} actionRow
 * @returns {{
 *   userId: string | null,
 *   email: string | null,
 *   linkType: string | null,
 *   actionLink: string | null,
 *   tokenHash: string | null,
 * }}
 */
export function summarizeRegenerateResult(actionRow) {
  const row = isPlainObject(actionRow) ? actionRow : {};
  const result = isPlainObject(row.result) ? row.result : {};
  const rawLink = typeof result.action_link === "string" ? result.action_link.trim() : "";
  const rawHash = typeof result.token_hash === "string" ? result.token_hash.trim() : "";
  const rawType = typeof result.link_type === "string" ? result.link_type.trim() : "";
  return {
    userId: stringOrNull(result.user_id),
    email: stringOrNull(result.email),
    linkType: rawType || null,
    actionLink: rawLink || null,
    tokenHash: rawHash || null,
  };
}

/**
 * Compose the prefetch-safe sign-in URL staff hand over, or null.
 *
 * The raw GoTrue action_link points at /auth/v1/verify, which SPENDS
 * the one-time token on a plain GET. A staff handoff usually travels
 * through email or Slack, where a scanner or an unfurl issues exactly
 * that GET and burns the link before the human clicks (the MER-523
 * root cause, AUTH_EMAIL_SETUP.md). The app form carries the token_hash
 * to /set-password, which verifies only on a deliberate click.
 *
 * Returns null when the executor returned no token_hash (older GoTrue)
 * or when the link type is not one /set-password verifies. Callers then
 * fall back to the raw action_link rather than showing nothing.
 *
 * @param {object} args  { origin, tokenHash, linkType }
 * @returns {string | null}
 */
export function buildSetPasswordLink(args) {
  const src = isPlainObject(args) ? args : {};
  const origin = trimmed(src.origin).replace(/\/+$/, "");
  const tokenHash = trimmed(src.tokenHash);
  const linkType = trimmed(src.linkType);
  if (!origin || !tokenHash) return null;
  if (!APP_LINK_TYPES.includes(linkType)) return null;
  const query = new URLSearchParams({ token_hash: tokenHash, type: linkType });
  return `${origin}/set-password?${query.toString()}`;
}
